import * as React from "react"
import { cn } from "@/lib/utils"
import { PremiumButton } from "./premium-button"
import { PremiumBadge } from "./premium-badge"

export interface NavigationItem {
  id: string
  label: string
  icon?: React.ReactNode
  href?: string
  badge?: {
    text: string
    variant?: "primary" | "accent" | "success" | "warning" | "destructive"
  }
  isActive?: boolean
  disabled?: boolean
  children?: NavigationItem[]
}

export interface PremiumNavigationProps {
  items: NavigationItem[]
  orientation?: "horizontal" | "vertical"
  variant?: "default" | "glass" | "minimal"
  className?: string
  onItemSelect?: (item: NavigationItem) => void
}

const PremiumNavigation = React.forwardRef<HTMLElement, PremiumNavigationProps>(
  ({ items, orientation = "horizontal", variant = "default", className, onItemSelect }, ref) => {
    const getNavContainerClasses = () => {
      const baseClasses = "flex transition-all duration-200"
      
      switch (orientation) {
        case "vertical":
          return cn(baseClasses, "flex-col space-y-1")
        default:
          return cn(baseClasses, "flex-row space-x-1")
      }
    }

    const getNavItemClasses = (item: NavigationItem) => {
      const baseClasses = "relative flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 group"
      
      if (item.disabled) {
        return cn(baseClasses, "opacity-50 cursor-not-allowed")
      }
      
      if (item.isActive) {
        switch (variant) {
          case "glass":
            return cn(baseClasses, "glass text-primary border-primary/20 shadow-sm")
          case "minimal":
            return cn(baseClasses, "text-primary bg-primary/10 border-l-2 border-primary")
          default:
            return cn(baseClasses, "text-primary-foreground bg-gradient-to-r from-primary to-primary/80 shadow-md hover:shadow-lg")
        }
      }
      
      // Non-active states
      switch (variant) {
        case "glass":
          return cn(baseClasses, "text-muted-foreground hover:glass hover:text-foreground hover:border-primary/20")
        case "minimal":
          return cn(baseClasses, "text-muted-foreground hover:text-foreground hover:bg-accent/5 hover:border-l-2 hover:border-accent/50")
        default:
          return cn(baseClasses, "text-muted-foreground hover:text-foreground hover:bg-secondary/50")
      }
    }

    const handleItemClick = (item: NavigationItem) => {
      if (item.disabled) return
      onItemSelect?.(item)
    }

    const renderNavigationItem = (item: NavigationItem, depth = 0) => (
      <div key={item.id} className={cn(depth > 0 && "ml-4")}>
        <button
          onClick={() => handleItemClick(item)}
          className={getNavItemClasses(item)}
          disabled={item.disabled}
        >
          {/* Icon */}
          {item.icon && (
            <span className={cn(
              "flex-shrink-0 transition-colors duration-200",
              item.isActive ? "text-current" : "text-muted-foreground group-hover:text-current"
            )}>
              {item.icon}
            </span>
          )}
          
          {/* Label */}
          <span className="flex-1 text-left truncate">
            {item.label}
          </span>
          
          {/* Badge */}
          {item.badge && (
            <PremiumBadge 
              variant={item.badge.variant || "primary"} 
              size="sm"
              className="ml-auto"
            >
              {item.badge.text}
            </PremiumBadge>
          )}
          
          {/* Active Indicator */}
          {item.isActive && variant === "default" && (
            <div className="absolute inset-0 rounded-lg ring-1 ring-inset ring-primary/20" />
          )}
          
          {/* Hover Effect */}
          <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-primary/0 to-accent/0 group-hover:from-primary/5 group-hover:to-accent/5 transition-all duration-200" />
        </button>
        
        {/* Children/Submenu */}
        {item.children && item.children.length > 0 && (
          <div className="mt-1">
            {item.children.map(child => renderNavigationItem(child, depth + 1))}
          </div>
        )}
      </div>
    )

    return (
      <nav ref={ref} className={cn("w-full", className)}>
        <div className={getNavContainerClasses()}>
          {items.map(item => renderNavigationItem(item))}
        </div>
      </nav>
    )
  }
)
PremiumNavigation.displayName = "PremiumNavigation"

// Sidebar Navigation Component
export interface PremiumSidebarProps {
  items: NavigationItem[]
  className?: string
  onItemSelect?: (item: NavigationItem) => void
  header?: React.ReactNode
  footer?: React.ReactNode
}

const PremiumSidebar = React.forwardRef<HTMLDivElement, PremiumSidebarProps>(
  ({ items, className, onItemSelect, header, footer }, ref) => (
    <div 
      ref={ref}
      className={cn(
        "flex flex-col h-full bg-card border-r border-border overflow-hidden",
        className
      )}
    >
      {/* Header */}
      {header && (
        <div className="flex-shrink-0 p-6 border-b border-border">
          {header}
        </div>
      )}
      
      {/* Navigation */}
      <div className="flex-1 overflow-y-auto p-4 scrollbar-premium">
        <PremiumNavigation
          items={items}
          orientation="vertical"
          variant="minimal"
          onItemSelect={onItemSelect}
        />
      </div>
      
      {/* Footer */}
      {footer && (
        <div className="flex-shrink-0 p-4 border-t border-border">
          {footer}
        </div>
      )}
    </div>
  )
)
PremiumSidebar.displayName = "PremiumSidebar"

export { PremiumNavigation, PremiumSidebar } 